import apiClient from '../api/apiClient';

/**
 * Authentication Service
 * - login: email + password -> backend sets auth cookies, returns user/roles/permissions
 * - refreshToken: exchange refresh cookie for new access token
 * - logout: POST /api/auth/logout (sends cookie), then clear local auth
 * - setupPassword: for reset/set password (token from email link + new password)
 */
class AuthService {
  /**
   * Login with email and password.
   * Backend sets access + refresh tokens in httpOnly cookies.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{ success, message, data }>}
   */
  async login(email, password) {
    const response = await apiClient.post('/api/auth/login', { email, password });
    const { data } = response;
    if (data?.success && data?.data?.user) {
      apiClient.setAuthData(data.data);
    }
    return data;
  }

  /**
   * Refresh access token using refresh token in httpOnly cookie.
   * Backend sets a new access token cookie.
   * @returns {Promise<{ success: boolean, error?: any }>}
   */
  async refreshToken() {
    try {
      await apiClient.post('/api/auth/refresh-token', {}, { withCredentials: true });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Logout: call backend to invalidate session (sends auth cookie via withCredentials),
   * then clear local user/permissions/roles.
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      await apiClient.post('/api/auth/logout', {}, { withCredentials: true });
    } finally {
      apiClient.clearAuth();
    }
  }

  /**
   * Request password reset email.
   * POST /api/auth/forgot-password with body { email }.
   * @param {string} email - User email address
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  async forgotPassword(email) {
    const response = await apiClient.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  /**
   * Set new password (e.g. after forgot-password email link).
   * POST /api/auth/setup-password with { token, password, confirm_password }.
   * @param {object} payload - { token, password, confirm_password }
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  async setupPassword(payload) {
    const response = await apiClient.post('/api/auth/setup-password', payload);
    return response.data;
  }

  /**
   * Reset password (forgot password flow).
   * POST /api/auth/reset-password with { token, password, confirm_password }.
   * @param {object} payload - { token, password, confirm_password }
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  async resetPassword(payload) {
    const response = await apiClient.post('/api/auth/reset-password', payload);
    return response.data;
  }
}

const authService = new AuthService();
export default authService;
